import { useEffect, useState } from 'react';

/**
 * Détection PROACTIVE d'un nouveau déploiement.
 *
 * Problème : la SPA est découpée en chunks JS hachés. Quand une nouvelle version
 * est déployée pendant qu'un onglet reste ouvert, les chunks de l'onglet n'existent
 * plus → au prochain chargement lazy (souvent dans l'admin), c'est la page blanche.
 *
 * Ce composant compare périodiquement (et au retour sur l'onglet) le hash du bundle
 * chargé avec celui servi en ligne. S'ils diffèrent → une nouvelle version est dispo
 * → on affiche une bannière « Recharger », AVANT que ça plante.
 *
 * (Le filet de sécurité reste ErrorBoundary/installChunkErrorListener, qui recharge
 * automatiquement si malgré tout un chunk périmé est demandé.)
 */

// Hash du bundle d'entrée actuellement chargé (ex. "index-D5SJMgX5.js").
const currentBundleHash = (): string | null => {
  const scripts = Array.from(
    document.querySelectorAll('script[src*="/assets/index-"]'),
  ) as HTMLScriptElement[];
  const src = scripts[0]?.src || '';
  const m = src.match(/index-[\w-]+\.js/);
  return m ? m[0] : null;
};

// Hash du bundle servi en ligne (depuis l'index.html frais, sans cache).
const fetchLatestHash = async (): Promise<string | null> => {
  try {
    const res = await fetch(`/?v=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const html = await res.text();
    const m = html.match(/index-[\w-]+\.js/);
    return m ? m[0] : null;
  } catch {
    return null;
  }
};

export const VersionWatcher = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    const current = currentBundleHash();
    // En dev (pas de bundle haché) ou structure inattendue : on ne surveille pas.
    if (!current) return;

    let done = false;
    const check = async () => {
      if (done || document.visibilityState !== 'visible') return;
      const latest = await fetchLatestHash();
      if (latest && latest !== current) {
        done = true;
        setUpdateAvailable(true);
      }
    };

    const interval = window.setInterval(check, 5 * 60 * 1000); // toutes les 5 min
    const onVisible = () => check(); // au retour sur l'onglet
    document.addEventListener('visibilitychange', onVisible);
    const first = window.setTimeout(check, 30_000); // premier check après 30 s

    return () => {
      done = true;
      window.clearInterval(interval);
      window.clearTimeout(first);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  if (!updateAvailable) return null;

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        bottom: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        background: '#1A2332',
        color: 'white',
        padding: '12px 16px',
        borderRadius: 10,
        boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        maxWidth: '92vw',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
        fontSize: 14,
        lineHeight: 1.3,
      }}
    >
      <span>Une nouvelle version du site est disponible.</span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{
          background: '#C9A96E',
          color: 'white',
          border: 'none',
          padding: '7px 14px',
          borderRadius: 8,
          cursor: 'pointer',
          fontSize: 14,
          fontWeight: 600,
          whiteSpace: 'nowrap',
        }}
      >
        Recharger
      </button>
    </div>
  );
};

export default VersionWatcher;
